import { useEffect, useState } from 'react';
import { ImageOff } from 'lucide-react';
import { Skeleton, cn } from '@eombor/ui';
import { apiClient } from '../../features/auth/model/auth.store.js';
import { t } from '../i18n/index.js';

/**
 * RUXSAT talab qiladigan rasm.
 *
 * Muammo shakli oddiy: token `Authorization` sarlavhasida yuradi, `<img src>`
 * esa sarlavha yubora olmaydi. Uchta yechim bor edi va tanlanganining sababi
 * ham qolgan ikkitasining narxida:
 *
 *   - **URL ichida token** — havola tarixga, jurnalga va `Referer` ga
 *     tushardi, ya'ni rasmni ko'rish uchun berilgan kalit butun tizimning
 *     kalitiga aylanardi;
 *   - **cookie** — butun autentifikatsiyani o'zgartirish va CSRF ni yangidan
 *     o'ylash degani, holbuki gap o'nta rasm haqida;
 *   - **baytlarni klient bilan olish** (shu yerdagi yo'l) — hech narsani
 *     o'zgartirmaydi va serverda ruxsat har so'rovda qayta tekshiriladi.
 *
 * Manzil NISBIY keladi (`files/<uuid>`) va uni API klienti o'zi to'ldiradi.
 * Mock rejimda esa `data:` keladi — u to'g'ridan-to'g'ri ishlatiladi, aks
 * holda mokda rasm o'rni bo'sh qolardi va alohida frontend dasturchisi
 * foto-hisobotni umuman ko'ra olmasdi.
 */

/** O'z-o'zicha ochiladigan manzil — klient orqali olishning hojati yo'q. */
const isDirect = (src: string): boolean =>
  src.startsWith('data:') || src.startsWith('blob:') || src.startsWith('http');

export interface AuthImageProps {
  /** `files/<uuid>` yoki tayyor `data:`/`blob:` manzil. */
  src: string;
  alt: string;
  className?: string;
  /** Yuklanayotgan payt egallaydigan joy — tayyor rasm bilan bir o'lchamda. */
  skeletonClassName?: string;
}

export const AuthImage = ({ src, alt, className, skeletonClassName }: AuthImageProps) => {
  const [url, setUrl] = useState<string | null>(() => (isDirect(src) ? src : null));
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (isDirect(src)) {
      setUrl(src);
      setFailed(false);
      return;
    }

    /*
     * `cancelled` — komponent baytlar kelgunga qadar yopilgan bo'lishi
     * mumkin (odam oynani darhol yopdi). Bunda `objectURL` yaratilmaydi
     * ham: yaratilsa, uni bo'shatadigan hech kim qolmasdi.
     */
    let cancelled = false;
    let objectUrl: string | null = null;

    setUrl(null);
    setFailed(false);

    void apiClient
      .apiBlob(`/${src}`)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      // Bo'shatilmasa har ochilgan rasm brauzer xotirasida MANGU qolardi:
      // o'nta rasmli dalolatnomani bir necha marta ochish yetarli.
      if (objectUrl !== null) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  if (failed) {
    return (
      <span
        role="img"
        aria-label={t('handover.photoUnavailable')}
        title={t('handover.photoUnavailable')}
        className={cn(
          'flex items-center justify-center bg-surface-2 text-text-faint',
          className,
          skeletonClassName,
        )}
      >
        <ImageOff size={18} aria-hidden />
      </span>
    );
  }

  if (url === null) return <Skeleton className={cn(className, skeletonClassName)} />;

  return <img src={url} alt={alt} loading="lazy" className={className} />;
};
